import React, { useEffect, useState } from 'react';
import { createScheduledMessage } from '../../../api/scheduledMessages';
import { createScheduledMessageAdmin } from '../../../api/scheduledMessagesAdmin';
import ContactSearchSelect from '../../../components/form/ContactSearchSelect';
import { getApiErrorMessage } from '../../../utils/errors';
import AgentFilterSelect from './AgentFilterSelect';
import ScheduledMessageFormFields from './ScheduledMessageFormFields';
import {
  DEFAULT_TIMEZONE,
  DEFAULT_BATCH_LEAD_DAYS,
} from '../constants';
import {
  buildDefaultRecurrenceFormFields,
  buildSchedulePayload,
  isValidRecurrenceForm,
} from '../utils';

const INITIAL_FORM = {
  contactId: '',
  templateName: '',
  generationInstruction: '',
  contentGenerationMode: 'runtime',
  batchLeadDays: String(DEFAULT_BATCH_LEAD_DAYS),
  scheduleType: 'one_off',
  scheduledAt: '',
  timezone: DEFAULT_TIMEZONE,
  endsAt: '',
  ...buildDefaultRecurrenceFormFields(),
};

function CreateScheduledMessageForm({ mode, onSuccess, onClose }) {
  const [formAgentId, setFormAgentId] = useState('');
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setForm((current) => ({ ...INITIAL_FORM, contactId: '' }));
    setError('');
    setSuccess('');
  }, [formAgentId]);

  const handleAgentChange = (event) => {
    setFormAgentId(event.target.value);
    setForm((current) => ({ ...current, contactId: '' }));
  };

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const setFormFields = (partial) => {
    setForm((current) => ({ ...current, ...partial }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!formAgentId) {
      setError('Selecione um agente antes de agendar.');
      return;
    }
    if (!form.contactId) {
      setError('Selecione um contato.');
      return;
    }
    if (!form.templateName.trim()) {
      setError('Informe o nome do template Botmaker.');
      return;
    }
    if (form.generationInstruction.trim().length < 10) {
      setError('Informe uma instrução para geração com pelo menos 10 caracteres.');
      return;
    }
    if (form.scheduleType === 'one_off' && !form.scheduledAt) {
      setError('Informe data e hora para agendamento único.');
      return;
    }
    if (form.scheduleType === 'recurring' && !isValidRecurrenceForm(form)) {
      setError(
        form.scheduleRecurrenceMode === 'advanced'
          ? 'Expressão cron inválida.'
          : 'Informe hora, minuto e pelo menos um dia da semana.'
      );
      return;
    }

    const payload = {
      contact_id: form.contactId,
      ...buildSchedulePayload(form),
    };

    setSaving(true);
    try {
      if (mode === 'admin') {
        await createScheduledMessageAdmin(formAgentId, payload);
      } else {
        await createScheduledMessage(formAgentId, payload);
      }
      setForm(INITIAL_FORM);
      setSuccess('Agendamento criado com sucesso.');
      const createdAgentId = formAgentId;
      setFormAgentId('');
      onSuccess?.(createdAgentId);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível criar o agendamento.'));
    } finally {
      setSaving(false);
    }
  };

  const fieldsDisabled = !formAgentId || saving;

  return (
    <div className="surface-card p-4 sm:p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-content-primary">Novo agendamento</h2>
          <p className="text-xs text-content-muted mt-1">
            O agente gera o conteúdo; o template Botmaker é usado quando a janela de 24h estiver fechada.
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary text-xs px-3 py-2 shrink-0"
          >
            Cancelar
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="space-y-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-content-muted">
            Destinatário
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <AgentFilterSelect
              id="schedule-agent"
              value={formAgentId}
              onChange={handleAgentChange}
              mode={mode}
              showAllOption={false}
              layout="stacked"
              disabled={saving}
            />
            <ContactSearchSelect
              id="schedule-contact"
              value={form.contactId}
              onChange={updateField('contactId')}
              agentId={formAgentId}
              mode={mode}
              disabled={saving}
            />
          </div>
        </section>

        <ScheduledMessageFormFields
          form={form}
          updateField={updateField}
          setFormFields={setFormFields}
          fieldsDisabled={fieldsDisabled}
        />

        {error && <p className="text-sm text-red-700">{error}</p>}
        {success && <p className="text-sm text-emerald-700">{success}</p>}

        <div className="flex justify-end border-t border-surface-border pt-4">
          <button
            type="submit"
            disabled={fieldsDisabled}
            className="btn-primary text-sm px-4 py-2 disabled:opacity-40"
          >
            {saving ? 'Agendando...' : 'Criar agendamento'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateScheduledMessageForm;
